/**
 * File: SectionPreview.tsx
 * Description: Component for previewing generated sections with view-only markdown display
 * Author: System
 * Created: 2025-08-09
 * Last Modified: 2025-08-10
 */

import { 
  CheckCircleIcon, 
  ClockIcon, 
  PhotoIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import * as React from 'react';

import { generationService } from '../../services/generationService';
import { GeneratedSection, ArchitectureSection, ProjectContext } from '../../types/generation';
import { logger } from '../../utils/logger';
import { getSectionInfo } from '../../utils/sectionMapping';
import { countWords } from '../../utils/wordCount';
import { FeedbackButtons } from '../chat';
import { MonacoMarkdownEditor } from '../monaco/MonacoMarkdownEditor';

import { ThreatModelingReport } from './ThreatModelingReport';

/**
 * Sentinel groundedness score indicating the value is still being calculated in
 * the background. The real score (0-100) replaces it once polling completes.
 */
const GROUNDEDNESS_PENDING_SCORE = -1;

interface SectionPreviewProps {
  sections: Record<string, GeneratedSection>;
  selectedSection: string | null;
  sectionsGenerating?: Set<ArchitectureSection>;
  pendingGroundedness?: Set<ArchitectureSection>;
  configuredSections: ArchitectureSection[];
  projectId?: number;
  projectContext?: ProjectContext;
  onSectionSelect: (_sectionId: string | null) => void;
  onGenerateSection?: (
    _section: ArchitectureSection, 
    _isRegeneration?: boolean,
    _customInstructions?: string,
  ) => Promise<void>;
  onUpdateSection?: (
    _section: ArchitectureSection,
    _content: string
  ) => Promise<void>;
  onGenerationStart?: (
    _section: ArchitectureSection,
    _userRequest: string,
    _isRegeneration: boolean
  ) => void;
  onGenerationComplete?: (
    _section: ArchitectureSection,
    _result: any
  ) => void;
}

export const SectionPreview: React.FC<SectionPreviewProps> = ({
  sections,
  selectedSection,
  sectionsGenerating,
  pendingGroundedness,
  configuredSections,
  projectId = -1,
  projectContext,
  onSectionSelect,
  onGenerateSection,
  onUpdateSection,
  onGenerationStart,
  onGenerationComplete,
}) => {
  const [editingSection, setEditingSection] = React.useState<string | null>(null);
  const [editedContent, setEditedContent] = React.useState<string>('');
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [showUngroundedDetails, setShowUngroundedDetails] = React.useState<boolean>(false);
  const [groundednessData, setGroundednessData] = React.useState<{
    score: number;
    ungrounded_items: Record<string, string>;
  } | null>(null);
  const [loadingGroundedness, setLoadingGroundedness] = React.useState<boolean>(false);
  const [executionPlan, setExecutionPlan] = React.useState<Array<Record<string, any>> | Array<string> | null>(null);
  const [loadingExecutionPlan, setLoadingExecutionPlan] = React.useState<boolean>(false);
  const [isExecutionPlanExpanded, setIsExecutionPlanExpanded] = React.useState<boolean>(false);
  
  // State for regenerate popup
  const [showRegenerateModal, setShowRegenerateModal] = React.useState<boolean>(false);
  const [regenerateInstructions, setRegenerateInstructions] = React.useState<string>('');
  const [regeneratingSection, setRegeneratingSection] = React.useState<ArchitectureSection | null>(null);
  
  // State for tracking generation context for feedback
  const [generationContext, setGenerationContext] = React.useState<{
    [sectionId: string]: {
      inputs: {
        section: ArchitectureSection;
        userRequest?: string;
        projectContext?: any;
        timestamp: string;
        isRegeneration: boolean;
      };
      outputs?: {
        content: string;
        groundednessScore: number;
        references: any[];
        processingTime?: number;
        success: boolean;
        timestamp: string;
      };
    };
  }>({});
  
  // Refs for feedback button positioning
  const generateButtonRef = React.useRef<HTMLButtonElement>(null);
  const ungroundedDetailsRef = React.useRef<HTMLDivElement>(null);

  // Function to scroll to ungrounded items section
  const scrollToUngroundedItems = () => {
    if (ungroundedDetailsRef.current) {
      ungroundedDetailsRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
      });
      // Expand the section if it's collapsed
      if (!showUngroundedDetails) {
        setShowUngroundedDetails(true);
      }
    }
  };

  // Feedback handler - actual submission is handled by FeedbackButtons component
  const handleFeedback = (messageId: string | undefined, type: 'up' | 'down', comment: string, includeContext: boolean, includeUserName: boolean) => {
  };

  // Handle opening regenerate modal
  const handleOpenRegenerateModal = (section: ArchitectureSection) => {
    setRegeneratingSection(section);
    setRegenerateInstructions('');
    setShowRegenerateModal(true);
  };

  // Close regenerate modal when parent generation confirms/cancels (so overlays
  // like the confirmation modal can appear and the UI is consistent)
  React.useEffect(() => {
    const handler = () => handleCloseRegenerateModal();
    globalThis.addEventListener('closeRegenerateModal', handler as EventListener);
    globalThis.addEventListener('globalCloseAllModals', handler as EventListener);
    return () => {
      globalThis.removeEventListener('closeRegenerateModal', handler as EventListener);
      globalThis.removeEventListener('globalCloseAllModals', handler as EventListener);
    };
  }, []);

  // Handle closing regenerate modal
  const handleCloseRegenerateModal = () => {
    setShowRegenerateModal(false);
    setRegenerateInstructions('');
    setRegeneratingSection(null);
  };

  // Handle regenerate with instructions
  const handleRegenerateWithInstructions = async () => {
    if (!regeneratingSection || !onGenerateSection) {return;}
    // ONLY pass instructions if user actually provided them
    // If empty, pass undefined to trigger full regeneration with specialized agent
    const instructions = regenerateInstructions.trim() || undefined;

    handleCloseRegenerateModal();

    try {
      void onGenerateSection(regeneratingSection, true, instructions);
    } catch (err) {
      logger.error('Error triggering regeneration:', err);
    }
  };

  // Helper function to get display name for a section
  const getSectionDisplayName = (section: ArchitectureSection): string => {
    const displayNames: Record<ArchitectureSection, string> = {
      [ArchitectureSection.BUSINESS_PURPOSE]: 'Business Purpose',
      [ArchitectureSection.SOLUTION_SUMMARY]: 'Solution Summary',
      [ArchitectureSection.ADR_SUMMARY]: 'ADR Summary',
      [ArchitectureSection.CONCEPTUAL_ARCHITECTURE]: 'Conceptual Architecture',
      [ArchitectureSection.DATA_ARCHITECTURE]: 'Data Architecture',
      [ArchitectureSection.INTEGRATION_ARCHITECTURE]: 'Integration Architecture',
      [ArchitectureSection.IMPACTED_COMPONENTS]: 'Impacted Components',
      [ArchitectureSection.SECURITY_ARCHITECTURE]: 'Security Architecture',
      [ArchitectureSection.TECHNOLOGY_ARCHITECTURE]: 'Technology Architecture',
      [ArchitectureSection.DEPLOYMENT_MODEL]: 'Deployment Model',
      [ArchitectureSection.RISK_ASSESSMENT]: 'Risk Assessment',
      [ArchitectureSection.THREAT_MODELING]: 'Threat Modeling',
    };
    return displayNames[section] || section;
  };

  // Handle starting edit mode
  const handleStartEdit = (section: GeneratedSection) => {
    setEditingSection(section.id);
    setEditedContent(section.content || '');
    setSaveError(null); // Clear any previous errors
  };

  // Handle canceling edit mode
  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditedContent('');
    setSaveError(null); // Clear any errors
  };

  // Handle saving edited content
  const handleSaveEdit = async () => {
    if (!editingSection || !onUpdateSection) {return;}
    
    const sectionToUpdate = Object.values(sections).find(s => s.id === editingSection);
    if (!sectionToUpdate) {return;}

    setIsSaving(true);
    setSaveError(null); // Clear any previous errors
    
    try {
      await onUpdateSection(sectionToUpdate.section, editedContent);
      setEditingSection(null);
      setEditedContent('');
    } catch (error) {
      // Handle error gracefully
      const errorMessage = error instanceof Error ? error.message : 'Failed to save section';
      setSaveError(errorMessage);
      // Don't close edit mode on error, let user try again
    } finally {
      setIsSaving(false);
    }
  };

  // Create a list of all configured sections with their current state
  const allSections = configuredSections.map(sectionType => {
    // Check if this section has been generated
    const sectionValues = Object.values(sections);
    const generatedSection = sectionValues.find(s => s.section === sectionType);
    
    if (generatedSection) {
      return generatedSection;
    } else {
      // Create a placeholder for non-generated sections
      return {
        id: `${sectionType}-placeholder`,
        section: sectionType,
        title: getSectionDisplayName(sectionType),
        content: '',
        status: 'pending' as const,
        groundednessScore: 0,
        references: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    }
  });
  
  // Find the current section by matching either the section key or the section ID
  const currentSection = selectedSection 
    ? sections[selectedSection] ?? allSections.find(s => s.id === selectedSection || s.section === selectedSection)
    : null;

  const currentSectionInfo = currentSection ? getSectionInfo(currentSection.section) : null;

  // General function to check staleness for any section
  const checkSectionStaleness = React.useCallback((section: GeneratedSection | null, context?: ProjectContext) => {
    if (!section?.updatedAt || !context) {
      return { isStale: false, sectionDate: null, latestDocDate: null };
    }

    // Validate that updatedAt is a valid Date
    if (!(section.updatedAt instanceof Date) || isNaN(section.updatedAt.getTime())) {
      return { isStale: false, sectionDate: null, latestDocDate: null };
    }

    // Normalize timestamp - ensure all timestamps are treated as UTC by adding Z if missing
    const normalizeTimestamp = (timestamp: string): string => {
      if (!timestamp) {return timestamp;}
      // If timestamp doesn't have Z or timezone offset, treat it as UTC by adding Z
      if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
        return `${timestamp}Z`;
      }
      return timestamp;
    };

    const timestampSources: Array<{ timestamp: string; source: string; sourceId?: string }> = [];

    // 1. Add uploaded documents timestamps
    const uploadedDocs = context.uploadedDocuments?.filter(
      (doc) => doc.updated_at && doc.updated_at !== '',
    ) ?? [];
    
    uploadedDocs.forEach((doc) => {
      timestampSources.push({
        timestamp: normalizeTimestamp(doc.updated_at ?? ''),
        source: 'document',
        sourceId: doc.filename ?? doc.id,
      });
    });

    // 2. Add instructions timestamp if available
    if (context.instructionsUpdatedAt) {
      timestampSources.push({
        timestamp: normalizeTimestamp(context.instructionsUpdatedAt),
        source: 'instructions',
      });
    }

    // 3. Add ADR timestamp if available
    if (context.adrUpdatedAt) {
      timestampSources.push({
        timestamp: normalizeTimestamp(context.adrUpdatedAt),
        source: 'adr',
      });
    }

    if (timestampSources.length === 0) {
      return { isStale: false, sectionDate: null, latestDocDate: null };
    }

    // Find the most recent context update
    const latestContext = timestampSources.reduce((latest, current) => {
      const latestDate = new Date(latest.timestamp);
      const currentDate = new Date(current.timestamp);
      return currentDate > latestDate ? current : latest;
    }, timestampSources[0]);

    const sectionTimestamp = section.updatedAt.toISOString();
    const sectionDate = new Date(sectionTimestamp);
    const latestContextDate = new Date(latestContext.timestamp);
    const isStale = latestContextDate > sectionDate;

    return {
      isStale: Boolean(isStale),
      sectionDate: sectionTimestamp,
      latestDocDate: latestContext.timestamp,
    };
  }, []);

  const checkStaleness = React.useCallback(() => {
    return checkSectionStaleness(currentSection, projectContext);
  }, [currentSection, projectContext, checkSectionStaleness]);

  const stalenessInfo = checkStaleness();

  // Fetch execution plan when expanded and section changes
  React.useEffect(() => {
    const fetchExecutionPlan = async () => {
      const hasValidSection = currentSection?.content;
      if (!isExecutionPlanExpanded || !hasValidSection || projectId < 0) {
        return;
      }

      setLoadingExecutionPlan(true);
      try {
        const plan = await generationService.getSectionExecutionPlan(
          projectId,
          currentSection.section,
        );
        setExecutionPlan(plan);
      } catch (error) {
        logger.error('Failed to fetch execution plan:', error);
        setExecutionPlan(null);
      } finally {
        setLoadingExecutionPlan(false);
      }
    };

    void fetchExecutionPlan();
  }, [currentSection, projectId, isExecutionPlanExpanded]);

  // Fetch groundedness data when expanded and section changes
  React.useEffect(() => {
    const fetchGroundedness = async () => {
      const hasValidSection = currentSection?.content;
      if (!showUngroundedDetails || !hasValidSection || projectId < 0) {
        return;
      }

      setLoadingGroundedness(true);
      try {
        const data = await generationService.getSectionGroundedness(
          projectId,
          currentSection.section,
        );
        setGroundednessData(data);
      } catch (error) {
        logger.error('Failed to fetch groundedness data:', error);
        setGroundednessData(null);
      } finally {
        setLoadingGroundedness(false);
      }
    };

    void fetchGroundedness();
  }, [currentSection, projectId, showUngroundedDetails]);

  // Reset execution plan expansion when section changes
  React.useEffect(() => {
    setIsExecutionPlanExpanded(false);
    setExecutionPlan(null);
  }, [currentSection]);

  // Reset groundedness expansion when section changes
  React.useEffect(() => {
    setShowUngroundedDetails(false);
    setGroundednessData(null);
  }, [currentSection]);

  // Format timestamp to user's local time
  const formatTimestamp = (isoString: string | null | undefined): string => {
    if (!isoString) {return 'Unknown';}
    try {
      const date = new Date(isoString);
      return date.toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-text-onSurface-strong">Section Preview</h3>
      
      <div className="grid grid-cols-12 gap-4">
        {/* Section List */}
        <div className="col-span-4">
          <div className="bg-background-section border border-border-subtle rounded-lg overflow-hidden h-full">
            <div className="p-3 border-b border-border-subtle">
              <h4 className="font-medium text-text-onSurface-strong">Architecture Sections ({allSections.length})</h4>
            </div>
            <div className="overflow-hidden">
              {allSections.length === 0 ? (
                <div className="p-4 text-center text-text-onSurface-subtle">
                  <p>No sections configured</p>
                  <p className="text-xs mt-1">Configure sections in analysis options</p>
                </div>
              ) : (
                allSections.map((section) => {
                  // For generated sections, find the section key
                  const sectionKey = section.content 
                    ? Object.keys(sections).find(key => sections[key].id === section.id)
                    : section.section;
                  const isSelected = selectedSection === sectionKey || 
                    selectedSection === section.id || selectedSection === section.section;
                  const sectionInfo = getSectionInfo(section.section);
                  const isGenerating = sectionsGenerating?.has(section.section) ?? false;
                  const isGenerated = section.content && section.content.trim().length > 0;
                  
                  const generatedSection = sectionKey ? sections[sectionKey] : null;
                  const sectionStaleness = generatedSection 
                    ? checkSectionStaleness(generatedSection, projectContext) 
                    : null;
                  const isStale = sectionStaleness?.isStale ?? false;
                  
                  return (
                    <div
                      key={section.id}
                      className={`border-b border-border-subtle last:border-b-0 ${
                        isSelected ? 'bg-accent-tertiary-subtle border-l-4 border-l-accent-tertiary-strong' : ''
                      }`}
                    >
                      <button
                        onClick={() => onSectionSelect(sectionKey || section.id || section.section)}
                        className="w-full text-left p-3 hover:bg-background-base transition-colors"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="font-medium text-text-onSurface-strong text-sm flex items-center gap-2 min-w-0 flex-1">
                            {sectionInfo.requiresDiagram && <PhotoIcon className="w-3 h-3 text-blue-600 flex-shrink-0" />}
                            <span className="truncate">{section.title}</span>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {isGenerating ? (
                              <div className="flex items-center gap-1">
                                <ArrowPathIcon className="w-4 h-4 text-blue-600 animate-spin" />
                                <span className="text-xs text-blue-600">Generating...</span>
                              </div>
                            ) : isGenerated ? (
                              <div className="flex items-center gap-1">
                                <CheckCircleIcon className="w-4 h-4 text-green-600" />
                                {isStale && (
                                  <ExclamationTriangleIcon 
                                    className="w-4 h-4 text-orange-500" 
                                    title="Section may be outdated - context has been updated since this section was generated"
                                  />
                                )}
                              </div>
                            ) : (
                              <ClockIcon className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-text-onSurface-subtle">
                          {isGenerated ? (
                            <span>
                              {countWords(section.content || '')} words
                              {' • '}
                              {section.groundednessScore === GROUNDEDNESS_PENDING_SCORE ? (
                                <span className="inline-flex items-center gap-1 text-yellow-600">
                                  <ArrowPathIcon className="w-3 h-3 animate-spin" />
                                  Calculating…
                                </span>
                              ) : (
                                `Groundedness: ${section.groundednessScore}%`
                              )}
                            </span>
                          ) : (
                            'Not generated yet'
                          )}
                        </div>
                        <div className="text-xs text-text-onSurface-subtle mt-1">
                          {isGenerated ? 'Completed' : 'Pending'}
                        </div>
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Content Preview */}
        <div className="col-span-8">
          {currentSection ? (
            <div className="bg-background-section border border-border-subtle rounded-lg overflow-hidden h-full flex flex-col">
              <div className="p-4 border-b border-border-subtle flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-text-onSurface-strong">{currentSection.title}</h4>
                      {currentSectionInfo?.requiresDiagram && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                          <PhotoIcon className="w-3 h-3" />
                          <span>Has Diagrams</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-text-onSurface-subtle mt-1">
                      {currentSection.content && currentSection.content.trim().length > 0
                        ? (currentSection.updatedAt instanceof Date && !isNaN(currentSection.updatedAt.getTime()))
                          ? `Last updated: ${formatTimestamp(currentSection.updatedAt.toISOString())}`
                          : 'Generated (timestamp unavailable)'
                        : 'Not generated yet'
                      }
                    </p>
                    {stalenessInfo.isStale && (
                      <p className="text-xs text-yellow-600 mt-1">
                        ⚠ Knowledge has been changed since this section was generated.
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Generate/Regenerate Button */}
                    {onGenerateSection && (
                      <button
                        ref={generateButtonRef}
                        onClick={() => {
                          const isRegeneration = Boolean(
                            currentSection.content && currentSection.content.trim().length > 0,
                          );
                          if (isRegeneration) {
                            // Show modal for regeneration with instructions
                            handleOpenRegenerateModal(currentSection.section);
                          } else {
                            // Direct generation for new sections
                            onGenerateSection(currentSection.section, false);
                          }
                        }}
                        disabled={sectionsGenerating?.has(currentSection.section)}
                        className={'inline-flex items-center gap-1 px-2 py-1 rounded text-xs ' + 
                          'font-medium transition-colors ' + 
                          `${sectionsGenerating?.has(currentSection.section)
                            ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                            : currentSection.content && currentSection.content.trim().length > 0
                              ? 'bg-yellow-50 text-yellow-700 border border-yellow-200 ' +
                                'hover:bg-yellow-100'
                              : 'bg-blue-50 text-blue-700 border border-blue-200 ' +
                                'hover:bg-blue-100'
                          }`}
                      >
                        {sectionsGenerating?.has(currentSection.section) ? (
                          <>
                            <ArrowPathIcon className="w-3 h-3 animate-spin" />
                            Generating...
                          </>
                        ) : currentSection.content && currentSection.content.trim().length > 0 ? (
                          <>
                            <ArrowPathIcon className="w-3 h-3" />
                            Regenerate
                          </>
                        ) : (
                          <>
                            <ArrowPathIcon className="w-3 h-3" />
                            Generate
                          </>
                        )}
                      </button>
                    )}

                    {/* Feedback Buttons for Generation */}
                    {currentSection.content && currentSection.content.trim().length > 0 && (
                      <div className="flex items-center">
                        <FeedbackButtons
                          messageId={`architecture-section-${currentSection.section}-${currentSection.id}`}
                          projectId={projectId}
                          feedbackContext={{
                            type: 'architecture_generation',
                            data: {
                              sectionType: 'architecture_generation',
                              sectionId: currentSection.section,
                              title: getSectionDisplayName(currentSection.section),
                              content: currentSection.content,
                              status: currentSection.status,
                              groundednessScore: currentSection.groundednessScore,
                            },
                          }}
                          onFeedback={handleFeedback}
                        />
                      </div>
                    )}                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      currentSection.content && currentSection.content.trim().length > 0
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-gray-50 text-gray-700 border border-gray-200'
                    }`}>
                      {currentSection.content && currentSection.content.trim().length > 0 ? 'Completed' : 'Pending'}
                    </span>
                    {currentSection.content && currentSection.content.trim().length > 0 && (
                      currentSection.groundednessScore === GROUNDEDNESS_PENDING_SCORE ? (
                        pendingGroundedness?.has(currentSection.section) ? (
                          <span className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200 animate-pulse">
                            <ArrowPathIcon className="w-3 h-3 animate-spin" />
                            Calculating...
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                            <PencilSquareIcon className="w-3 h-3" />
                            Manually Edited
                          </span>
                        )
                      ) : (
                        <button
                          onClick={scrollToUngroundedItems}
                          className="px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 cursor-pointer transition-colors"
                          title="Click to view groundedness details"
                        >
                        Groundedness: {currentSection.groundednessScore}%
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
              
              {/* Error Display */}
              {saveError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mx-4">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-red-800 text-sm font-medium">Save Error</span>
                    <button
                      onClick={() => setSaveError(null)}
                      className="ml-auto text-red-600 hover:text-red-800"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-red-700 text-sm mt-1">{saveError}</p>
                </div>
              )}
              
              <div className="relative flex-1 overflow-hidden">
                <div className="h-full">
                  {currentSection.content && currentSection.content.trim().length > 0 ? (
                    currentSection.section ===
                    ArchitectureSection.THREAT_MODELING &&
                    editingSection !== currentSection.id ? (
                        <div className="h-full overflow-y-auto p-6 bg-base-200/30">
                          <ThreatModelingReport content={currentSection.content} />
                        </div>
                      ) : (
                        <MonacoMarkdownEditor
                          value={
                            editingSection === currentSection.id
                              ? editedContent
                              : (currentSection.content || '').startsWith(
                                `# ${currentSection.title}`,
                              )
                                ? (currentSection.content || '').replace(
                                  `# ${currentSection.title}\n`,
                                  '',
                                )
                                : (currentSection.content || '')
                          }
                          onChange={editingSection === currentSection.id ? setEditedContent : () => { /* Read-only */ }}
                          height="100%"
                          theme="light"
                          previewOnly={false}
                          iconOnlyButtons={true}
                          placeholder="No content available"
                          isEditable={Boolean(onUpdateSection)}
                          isEditing={editingSection === currentSection.id}
                          section={currentSection.section}
                          onMermaidFix={async (fixed) => {
                            await onUpdateSection?.(
                              currentSection.section,
                              fixed,
                            );
                          }}
                          onEditModeChange={(editing) => {
                            if (editing) {
                              handleStartEdit(currentSection);
                            } else {
                              handleCancelEdit();
                            }
                          }}
                          onSave={handleSaveEdit}
                          isSaving={isSaving}
                        />
                      )
                  ) : (
                    <div className="h-full flex items-center justify-center bg-background-base">
                      <div className="text-center text-text-onSurface-subtle">
                        <div className="w-16 h-16 mx-auto mb-4 bg-background-section rounded-lg flex items-center justify-center">
                          <ArrowPathIcon className="w-8 h-8 text-text-onSurface-subtle" />
                        </div>
                        <p className="text-lg font-medium mb-2">Section not generated yet</p>
                        <p className="text-sm">Click the &quot;Generate&quot; button above to create content for this section</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Section Info */}
              {currentSection.content && currentSection.content.trim().length > 0 && (
                <>
                  <div className="p-4 border-t border-border-subtle bg-background-base flex-shrink-0">
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <span className="text-text-onSurface-subtle">Word Count:</span>
                        <span className="ml-1 font-medium text-text-onSurface-strong">{countWords(currentSection.content || '')}</span>
                      </div>
                      <div>
                        <span className="text-text-onSurface-subtle">Groundedness:</span>
                        <span className="ml-1 font-medium text-text-onSurface-strong">{currentSection.groundednessScore}%</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Quality Footer */}
                  <div className="p-4 border-t border-border-subtle bg-background-section flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h5 className="text-sm font-medium text-text-onSurface-strong">Quality Assessment</h5>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-background-base border border-border-subtle rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all ${
                                currentSection.groundednessScore >= 80 ? 'bg-green-500' :
                                  currentSection.groundednessScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${currentSection.groundednessScore}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-text-onSurface-strong">
                            {currentSection.groundednessScore}%
                          </span>
                          <span className="text-xs text-text-onSurface-subtle">Groundedness</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          currentSection.groundednessScore >= 80 
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : currentSection.groundednessScore >= 60
                              ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                              : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                          {currentSection.groundednessScore >= 80 ? 'high' : currentSection.groundednessScore >= 60 ? 'medium' : 'low'} confidence
                        </span>
                        {(currentSection.references || []).length > 0 && (
                          <span className="text-xs text-text-onSurface-subtle">
                            {(currentSection.references || []).length} reference{(currentSection.references || []).length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Groundedness Details Section */}
                  <div ref={ungroundedDetailsRef} className="border-t border-border-subtle bg-background-base flex-shrink-0">
                    <button
                      onClick={() => setShowUngroundedDetails(!showUngroundedDetails)}
                      className="w-full p-4 flex items-center justify-between hover:bg-background-section transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <h5 className="text-sm font-medium text-text-onSurface-strong">Groundedness Details</h5>
                        {loadingGroundedness && (
                          <ArrowPathIcon className="w-4 h-4 text-orange-600 animate-spin" />
                        )}
                      </div>
                      <svg
                        className={`w-5 h-5 text-text-onSurface-subtle transition-transform ${
                          showUngroundedDetails ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showUngroundedDetails && (
                      <div className="px-4 pb-4">
                        {loadingGroundedness ? (
                          <div className="text-xs text-text-onSurface-subtle">
                            Loading groundedness details...
                          </div>
                        ) : groundednessData?.ungrounded_items && 
                           Object.keys(groundednessData.ungrounded_items).length > 0 ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-xs">
                                <ExclamationTriangleIcon className="w-4 h-4 text-orange-600" />
                                <span className="font-medium text-orange-900">
                                  {Object.keys(groundednessData.ungrounded_items).length} ungrounded sentence{Object.keys(groundednessData.ungrounded_items).length !== 1 ? 's' : ''}
                                </span>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs border border-orange-200 rounded">
                                  <thead className="bg-orange-50">
                                    <tr className="border-b border-orange-200">
                                      <th className="text-left py-2 px-2 font-semibold text-orange-900">Ungrounded Text</th>
                                      <th className="text-left py-2 px-2 font-semibold text-orange-900">Reason</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white">
                                    {Object.entries(groundednessData.ungrounded_items).map(([sentence, reason]) => (
                                      <tr key={`${sentence}-${reason}`} className="border-b border-orange-100 hover:bg-orange-50 transition-colors">
                                        <td className="py-2 px-2 text-orange-800">{sentence}</td>
                                        <td className="py-2 px-2 text-orange-700">{reason}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-text-onSurface-subtle">
                              No ungrounded items found for this section
                            </p>
                          )}
                      </div>
                    )}
                  </div>
                  {/* Execution Plan Footer */}
                  <div className="border-t border-border-subtle bg-background-base flex-shrink-0">
                    <button
                      onClick={() => setIsExecutionPlanExpanded(!isExecutionPlanExpanded)}
                      className="w-full p-4 flex items-center justify-between hover:bg-background-section transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <h5 className="text-sm font-medium text-text-onSurface-strong">Execution Plan</h5>
                        {loadingExecutionPlan && (
                          <ArrowPathIcon className="w-4 h-4 text-blue-600 animate-spin" />
                        )}
                      </div>
                      <svg
                        className={`w-5 h-5 text-text-onSurface-subtle transition-transform ${
                          isExecutionPlanExpanded ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isExecutionPlanExpanded && (
                      <div className="px-4 pb-4">
                        {loadingExecutionPlan ? (
                          <div className="text-xs text-text-onSurface-subtle">Loading execution plan...</div>
                        ) : executionPlan && executionPlan.length > 0 ? (
                          <div className="space-y-4">
                            {executionPlan.map((step, index) => (
                              <div key={index} className="border border-border-subtle rounded-lg p-3 bg-background-section">
                                <div className="flex items-start gap-2">
                                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-medium text-xs">
                                    {index + 1}
                                  </span>
                                  <div className="flex-1 min-w-0 space-y-2">
                                    {typeof step === 'string' ? (
                                      <p className="text-xs text-text-onSurface-strong break-words">{step}</p>
                                    ) : (
                                      <>
                                        {/* Agent Name */}
                                        {step.agent_name && (
                                          <div className="flex items-center gap-2">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium">
                                              {step.agent_name}
                                            </span>
                                          </div>
                                        )}
                                        
                                        {/* Output */}
                                        {step.output && (
                                          <div className="space-y-1">
                                            <p className="text-xs font-medium text-text-onSurface-strong">Output:</p>
                                            <p className="text-xs text-text-onSurface-subtle whitespace-pre-wrap break-words">{step.output}</p>
                                          </div>
                                        )}
                                        
                                        {/* Reasoning */}
                                        {step.reasoning && (
                                          <div className="space-y-1">
                                            <p className="text-xs font-medium text-text-onSurface-strong">Reasoning:</p>
                                            <p className="text-xs text-text-onSurface-subtle whitespace-pre-wrap break-words">{step.reasoning}</p>
                                          </div>
                                        )}
                                        
                                        {/* Tool Calls */}
                                        {step.tool_calls && step.tool_calls.length > 0 && (
                                          <div className="space-y-2">
                                            <p className="text-xs font-medium text-text-onSurface-strong">Tool Calls:</p>
                                            <div className="space-y-2">
                                              {step.tool_calls.map((tool: any, toolIndex: number) => (
                                                <div key={toolIndex} className="border-l-2 border-blue-200 pl-3 space-y-1 min-w-0">
                                                  <p className="text-xs font-medium text-blue-700 break-words">{tool.tool_name}</p>
                                                  {tool.tool_input && tool.tool_input !== '{}' && (
                                                    <div className="space-y-0.5">
                                                      <p className="text-xs font-medium text-text-onSurface-subtle">Input:</p>
                                                      <pre className="text-xs text-text-onSurface-subtle bg-background-base p-2 rounded overflow-x-auto whitespace-pre-wrap break-words max-w-full">
                                                        {typeof tool.tool_input === 'string' ? tool.tool_input : JSON.stringify(tool.tool_input, null, 2)}
                                                      </pre>
                                                    </div>
                                                  )}
                                                  {tool.tool_output && (
                                                    <div className="space-y-0.5">
                                                      <p className="text-xs font-medium text-text-onSurface-subtle">Output:</p>
                                                      <pre className="text-xs text-text-onSurface-subtle bg-background-base p-2 rounded overflow-x-auto whitespace-pre-wrap break-words max-w-full max-h-40">
                                                        {typeof tool.tool_output === 'string' ? tool.tool_output : JSON.stringify(tool.tool_output, null, 2)}
                                                      </pre>
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Fallback for simple objects */}
                                        {!step.agent_name && !step.output && !step.reasoning && !step.tool_calls && (
                                          <>
                                            {step.description && (
                                              <p className="text-xs text-text-onSurface-strong font-medium">{step.description}</p>
                                            )}
                                            {step.details && (
                                              <p className="text-xs text-text-onSurface-subtle mt-1">{step.details}</p>
                                            )}
                                          </>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-text-onSurface-subtle">No execution plan available for this section</p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="bg-background-section border border-border-subtle rounded-lg p-8 text-center h-full flex items-center justify-center">
              <div className="text-text-onSurface-subtle">
                <svg className="w-12 h-12 mx-auto mb-4 text-text-onSurface-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg mb-2">Select a section to preview</p>
                <p className="text-sm">Choose a section from the list to view its generated content and quality metrics</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Regenerate Modal */}
      {showRegenerateModal && regeneratingSection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border-subtle">
              <h3 className="text-lg font-semibold text-text-onSurface-strong">
                Regenerate: {getSectionDisplayName(regeneratingSection)}
              </h3>
              <button
                onClick={handleCloseRegenerateModal}
                className="text-text-onSurface-subtle hover:text-text-onSurface-strong transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-text-onSurface-subtle mb-4">
                  Add specific instructions for regenerating this section. The AI will use your 
                  instructions along with the project context to create improved content.
                </p>
                {sections[regeneratingSection]?.groundednessScore === -1 &&
                  !pendingGroundedness?.has(regeneratingSection as ArchitectureSection) && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700 font-medium">
                      ⚠️ Warning: This section has been manually edited. Regenerating will overwrite all manual changes.
                    </p>
                  </div>
                )}
                <label htmlFor="regenerate-instructions" className="block text-sm font-medium text-text-onSurface-strong mb-2">
                  Instructions (Optional)
                </label>
                <textarea
                  id="regenerate-instructions"
                  value={regenerateInstructions}
                  onChange={(e) => setRegenerateInstructions(e.target.value)}
                  placeholder="e.g., 'Focus more on security aspects', 'Make it more concise', 'Add more technical details'..."
                  className="w-full h-32 px-3 py-2 border border-border-subtle rounded-lg 
                    focus:outline-none focus:ring-2 focus:ring-accent-primary-strong focus:border-transparent
                    text-sm text-text-onSurface-strong placeholder-text-onSurface-subtle
                    resize-none"
                />
                <p className="text-xs text-text-onSurface-subtle mt-2">
                  Leave empty to regenerate with standard instructions
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border-subtle bg-background-base">
              <button
                onClick={handleCloseRegenerateModal}
                className="px-4 py-2 text-sm font-medium text-text-onSurface-strong 
                  bg-white border border-border-subtle rounded-lg
                  hover:bg-background-base transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerateWithInstructions}
                className="px-4 py-2 text-sm font-medium text-white 
                  bg-accent-primary-strong rounded-lg
                  hover:bg-accent-primary-stronger transition-colors
                  inline-flex items-center gap-2"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Regenerate Section
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
